import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, imageUrl } from "../lib/api";
import { useBrand } from "../context/BrandContext";
import PublicLayout from "../components/PublicLayout";
import Seo from "../components/Seo";
import InfinityMotif from "../components/InfinityMotif";
import {
  ArrowUpRight,
  Broadcast,
  DeviceMobileCamera,
  Lightning,
  GlobeStand,
  WifiHigh,
  ChatsCircle,
  Star,
  ArrowDown,
} from "@phosphor-icons/react";

const FALLBACK_HERO_IMAGES = [
  "/hero-rainbow.jpg",
  "https://images.unsplash.com/photo-1767047222492-15b36d4b3dcb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
];

/* =========================================================
   HERO — centered, editorial, bioastra-style
   ========================================================= */
const Hero = ({ slides }) => {
  const [i, setI] = useState(0);
  const list = slides.length ? slides : null;
  const { hero_url } = useBrand();

  useEffect(() => {
    if (!list || list.length <= 1) return;
    const t = setInterval(() => setI((p) => (p + 1) % list.length), 7000);
    return () => clearInterval(t);
  }, [list]);

  const activeSlide = list ? list[i] : null;
  const heroImage = activeSlide?.image_path
    ? imageUrl(activeSlide.image_path)
    : hero_url || FALLBACK_HERO_IMAGES[i % FALLBACK_HERO_IMAGES.length];
  const heroTitle = activeSlide?.title || "Turning telecoms complexity into clarity. For South African business.";
  const heroSubtitle =
    activeSlide?.subtitle ||
    "Infinity DMS negotiates, deploys and manages mobile, fibre and voice contracts with MTN, Vodacom, Cell C, Vox and Telkom — so your team stays connected without the admin.";
  const heroCta = activeSlide?.cta_label || "Explore Offers";
  const heroLink = activeSlide?.cta_link || "/offers";

  return (
    <section className="relative pt-10 pb-32 lg:pb-40 overflow-hidden" data-testid="home-hero">
      {/* Rainbow glow behind hero */}
      <div
        className="absolute -top-20 left-1/2 -translate-x-1/2 w-[90vw] h-[360px] rainbow-swoosh pointer-events-none"
        aria-hidden
      />

      <div className="relative max-w-[1100px] mx-auto px-6 text-center">
        {/* SCROLL DOWN indicator (bioastra signature) */}
        <div className="absolute left-4 top-28 hidden lg:flex flex-col items-center gap-4 opacity-60">
          <span className="scroll-down">SCROLL DOWN</span>
          <ArrowDown size={14} className="text-[#a8b2cc]" />
        </div>

        <div className="flex justify-center mb-8 fade-in">
          <Star size={28} weight="fill" className="text-[#dda632]" />
        </div>

        <div className="mx-auto mb-10 w-40 h-[6px] rounded-full bg-gradient-to-r from-[#55a2d2] via-[#af3fa6] to-[#dda632]" />

        <h1
          className="font-display text-5xl sm:text-6xl lg:text-[5.6rem] leading-[0.96] max-w-5xl mx-auto fade-up"
          data-testid="hero-title"
        >
          {heroTitle.includes(".") ? (
            <>
              {heroTitle.split(".")[0]}.
              <span className="rainbow-text"> {heroTitle.split(".").slice(1).join(".").trim()}</span>
            </>
          ) : (
            heroTitle
          )}
        </h1>

        <p className="mt-10 text-xl text-[#a8b2cc] leading-relaxed max-w-2xl mx-auto fade-up" style={{ animationDelay: "120ms" }}>
          {heroSubtitle}
        </p>

        <div className="mt-12 flex flex-wrap gap-4 justify-center fade-up" style={{ animationDelay: "240ms" }}>
          <Link to={heroLink} className="btn-primary" data-testid="hero-primary-cta">
            {heroCta} <ArrowUpRight size={14} weight="bold" />
          </Link>
          <Link to="/build-package" className="btn-outline" data-testid="hero-build-cta">
            Build Your Package
          </Link>
        </div>

        {/* Hero image frame */}
        <div
          className="mt-20 relative mx-auto max-w-[1040px] rounded-[40px] overflow-hidden shadow-[0_40px_80px_-40px_rgba(11,17,32,0.35)] fade-up"
          style={{ animationDelay: "360ms" }}
        >
          <div className="aspect-[16/9] bg-[#141e32]">
            <img
              src={heroImage}
              alt="Infinity DMS connectivity"
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.src = FALLBACK_HERO_IMAGES[0]; }}
            />
          </div>
          {list && list.length > 1 && (
            <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between text-white">
              <div className="flex gap-1.5">
                {list.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setI(idx)}
                    className={`h-1.5 rounded-full transition-all ${
                      idx === i ? "w-10 bg-white" : "w-5 bg-white/40"
                    }`}
                    data-testid={`hero-dot-${idx}`}
                    aria-label={`Slide ${idx + 1}`}
                  />
                ))}
              </div>
              <span className="font-mono-accent text-[0.7rem] tracking-[0.28em] uppercase opacity-80">
                {String(i + 1).padStart(2, "0")} / {String(list.length).padStart(2, "0")}
              </span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

/* =========================================================
   WHAT WE DO — centered intro paragraph
   ========================================================= */
const WhatWeDo = () => (
  <section className="py-24 lg:py-32 text-center" data-testid="what-we-do">
    <div className="max-w-[820px] mx-auto px-6">
      <div className="eyebrow mb-5">Inside Infinity</div>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] text-[#f5ecd9]">
        Since 2020 — backed by <span className="rainbow-text">50+ years of combined ICT experience</span> —
        we are an approved partner for South Africa's leading networks, delivering seamless
        connectivity and IT for SoHo, SMMEs, enterprise and JSE-listed companies alike.
      </h2>
    </div>
  </section>
);

/* =========================================================
   PILLARS — 3 stacked, centered, editorial
   ========================================================= */
const PILLARS = [
  {
    icon: DeviceMobileCamera,
    title: "Connectivity",
    copy: "Business fibre, LTE, 5G, satellite, SD-WAN, APN and VoIP — across MTN, Vodacom, Cell C, Vox and Telkom.",
  },
  {
    icon: WifiHigh,
    title: "Cloud & IT",
    copy: "Cloud hosting on AWS, Azure, Huawei, Google or Zadara. Office 365, Teams Calling, cybersecurity and video collaboration.",
  },
  {
    icon: Lightning,
    title: "Smart Business",
    copy: "IoT, AI dashcams & fleet management, digital pen, unified comms, CCTV, access control, office automation and solar.",
  },
];

const Pillars = () => {
  const { pillar_connectivity_url, pillar_cloud_url, pillar_smart_url } = useBrand();
  const ART = [pillar_connectivity_url, pillar_cloud_url, pillar_smart_url];
  return (
  <section className="py-24 lg:py-28 relative overflow-hidden" data-testid="pillars">
    {/* Ambient infinity watermark behind pillars */}
    <InfinityMotif
      variant="outline"
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1400px] max-w-[140vw] h-auto"
      opacity={0.06}
    />
    <div className="relative max-w-[1180px] mx-auto px-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-14">
        {PILLARS.map((p, idx) => {
          const Icon = p.icon;
          const art = ART[idx];
          return (
            <div key={p.title} className="text-center fade-up" style={{ animationDelay: `${idx * 120}ms` }} data-testid={`pillar-${idx}`}>
              {art ? (
                <div className="mx-auto mb-7 w-32 h-32 rounded-[28px] overflow-hidden border border-[#2c3854] bg-[#141e32]">
                  <img src={art} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                </div>
              ) : (
                <div className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-[#141e32] mb-7">
                  <Icon size={28} weight="duotone" className="text-[#f5ecd9]" />
                </div>
              )}
              <h3 className="font-display text-2xl lg:text-3xl text-[#f5ecd9] mb-4">{p.title}</h3>
              <p className="text-[#a8b2cc] leading-relaxed text-lg max-w-sm mx-auto">{p.copy}</p>
            </div>
          );
        })}
      </div>
      <div className="mt-20 text-center">
        <Link to="/services" className="btn-link" data-testid="pillars-link">
          Learn more about services <ArrowUpRight size={12} weight="bold" />
        </Link>
      </div>
    </div>
  </section>
  );
};

/* =========================================================
   RAINBOW BAND — signature bioastra divider
   ========================================================= */
const RainbowBand = () => <div className="rainbow-bar rainbow-bar-xl" />;

/* =========================================================
   PROGRAM OVERVIEW — offers preview (bioastra "Program Overview")
   ========================================================= */
const ProgramOverview = ({ offers }) => {
  const top = offers.slice(0, 3);

  return (
    <section className="py-28 lg:py-36 bg-[#070b14]" data-testid="program-overview">
      <div className="max-w-[1180px] mx-auto px-6">
        <div className="max-w-[820px] mx-auto text-center mb-20">
          <div className="eyebrow mb-5">Program Overview</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.05] text-[#f5ecd9]">
            Current specials from every major <span className="rainbow-text">SA carrier</span>.
          </h2>
          <p className="mt-8 text-xl text-[#a8b2cc] leading-relaxed">
            We're constantly negotiating new deals for our clients. Below are a few hot specials
            running right now — or build a custom package and we'll find you something even better.
          </p>
        </div>

        {top.length === 0 ? (
          <div className="text-center py-10">
            <div className="inline-flex items-center gap-2 text-[#a8b2cc]">
              New deals launching weekly — check back soon.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {top.map((o) => (
              <article key={o.id} className="soft-card group flex flex-col" data-testid={`offer-card-${o.id}`}>
                <div className="relative aspect-[5/4] overflow-hidden bg-[#141e32]">
                  {o.image_path ? (
                    <img
                      src={imageUrl(o.image_path)}
                      alt={o.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-mono-accent text-[#a8b2cc]">
                      {o.provider.toUpperCase()}
                    </div>
                  )}
                  {o.badge && (
                    <span className="absolute top-4 left-4 brand-chip bg-[#af3fa6] text-white">
                      {o.badge}
                    </span>
                  )}
                </div>
                <div className="p-7 flex-1 flex flex-col">
                  <div className="eyebrow mb-3">{o.provider}</div>
                  <h3 className="font-display text-2xl text-[#f5ecd9] mb-3 leading-tight">{o.title}</h3>
                  <p className="text-[#a8b2cc] leading-relaxed flex-1 line-clamp-3">{o.description}</p>
                  <div className="mt-6 flex items-center justify-between">
                    {o.price && <div className="font-display text-xl text-[#f5ecd9]">{o.price}</div>}
                    <Link to={`/offers/${o.id}`} className="btn-link" data-testid={`offer-enquire-${o.id}`}>
                      Enquire <ArrowUpRight size={12} weight="bold" />
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        <div className="mt-16 text-center">
          <Link to="/offers" className="btn-primary" data-testid="program-view-all">
            View all offers <ArrowUpRight size={14} weight="bold" />
          </Link>
        </div>
      </div>
    </section>
  );
};

/* =========================================================
   BUILD PACKAGE INVITE — editorial
   ========================================================= */
const BuildInvite = () => (
  <section className="py-28 lg:py-36 relative overflow-hidden" data-testid="build-invite">
    <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[320px] rainbow-swoosh opacity-40 pointer-events-none" />
    <div className="max-w-[900px] mx-auto px-6 text-center relative">
      <div className="eyebrow mb-5">Build your own</div>
      <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.8rem] leading-[1.02] text-[#f5ecd9]">
        Don't see your perfect deal?<br />
        <span className="rainbow-text">We'll build it.</span>
      </h2>
      <p className="mt-8 text-xl text-[#a8b2cc] leading-relaxed max-w-xl mx-auto">
        Tell us how much you call, how much you scroll, and which network you trust. Under two minutes.
      </p>
      <div className="mt-12">
        <Link to="/build-package" className="btn-primary" data-testid="build-invite-cta">
          Build My Package <ArrowUpRight size={14} weight="bold" />
        </Link>
      </div>
    </div>
  </section>
);

/* =========================================================
   PARTNERS — bioastra "Collaborators & Partners" logo strip
   ========================================================= */
const PARTNER_TAGS = [
  { name: "MTN", accent: "#ffcc00" },
  { name: "Vodacom", accent: "#e60000" },
  { name: "Cell C", accent: "#6c2d91" },
  { name: "Vox", accent: "#1fa9e0" },
  { name: "Telkom", accent: "#003da5" },
  { name: "AWS", accent: "#ff9900" },
  { name: "Azure", accent: "#0078d4" },
  { name: "Huawei", accent: "#c7000b" },
  { name: "Google", accent: "#4285f4" },
  { name: "Zadara", accent: "#00a3e0" },
];

const Partners = () => (
  <section className="py-24 border-t border-[#2c3854]" data-testid="partners">
    <div className="max-w-[1180px] mx-auto px-6 text-center">
      <div className="eyebrow mb-12">Collaborators &amp; Technology Partners</div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-5 lg:gap-6">
        {PARTNER_TAGS.map((p) => (
          <div
            key={p.name}
            className="py-9 px-4 border border-[#2c3854] rounded-2xl bg-[#141e32] flex flex-col items-center justify-center gap-3 hover:shadow-[0_20px_40px_-24px_rgba(11,17,32,0.25)] transition-shadow"
          >
            <div
              className="w-3 h-3 rounded-full"
              style={{ background: p.accent }}
              aria-hidden
            />
            <div className="font-display text-xl text-[#f5ecd9]">{p.name}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* =========================================================
   FINAL CTA — dark editorial block
   ========================================================= */
const FinalCTA = () => (
  <section className="py-28 lg:py-36 bg-[#141e32] text-white relative overflow-hidden" data-testid="final-cta">
    <div className="absolute top-0 left-0 right-0 rainbow-bar" />
    <div className="absolute -right-20 -top-20 w-[480px] h-[480px] rainbow-swoosh pointer-events-none" />
    <div className="relative max-w-[900px] mx-auto px-6 text-center">
      <div className="eyebrow mb-5" style={{ color: "#dda632" }}>Ready when you are</div>
      <h2 className="font-display text-4xl sm:text-5xl lg:text-[4rem] leading-[1.02]">
        Let's audit your telecoms bill — <span className="rainbow-text">no strings attached.</span>
      </h2>
      <p className="mt-8 text-xl text-[#a8b2cc] leading-relaxed max-w-xl mx-auto">
        Most clients save within the first month. Send us your latest invoice — we'll review it
        within 48 hours and show you exactly where to save.
      </p>
      <div className="mt-12 flex flex-wrap gap-4 justify-center">
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 bg-[#f5ecd9] text-[#070b14] rounded-full px-7 py-4 font-mono-accent text-[0.78rem] tracking-[0.22em] uppercase font-semibold hover:bg-[#dda632] transition-colors"
          data-testid="final-cta-contact"
        >
          Start the Audit <ArrowUpRight size={14} weight="bold" />
        </Link>
        <a
          href="tel:0718889055"
          className="inline-flex items-center gap-2 border border-white/40 text-white rounded-full px-7 py-4 font-mono-accent text-[0.78rem] tracking-[0.22em] uppercase font-semibold hover:bg-[#f5ecd9] hover:text-[#070b14] transition-colors"
          data-testid="final-cta-phone"
        >
          071 888 9055
        </a>
      </div>
    </div>
  </section>
);

export default function HomePage() {
  const [slides, setSlides] = useState([]);
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    api.get("/slides").then((r) => setSlides(r.data || [])).catch(() => {});
    api.get("/offers").then((r) => setOffers(r.data || [])).catch(() => {});
  }, []);

  return (
    <PublicLayout>
      <Seo path="/" />
      <div data-testid="home-page">
        <Hero slides={slides} />
        <RainbowBand />
        <WhatWeDo />
        <Pillars />
        <RainbowBand />
        <ProgramOverview offers={offers} />
        <BuildInvite />
        <Partners />
        <FinalCTA />
      </div>
    </PublicLayout>
  );
}
